import { PropsWithChildren } from "react";
import { Box, Divider, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { Icon } from "@iconify/react";
import undoIcon from "@iconify/icons-mdi/undo";
import redoIcon from "@iconify/icons-mdi/redo";
import identifierIcon from "@iconify/icons-mdi/identifier";
import qrcodeIcon from "@iconify/icons-mdi/qrcode";
import fileExcel from "@iconify/icons-mdi/file-excel";
import floorPlanIcon from "@iconify/icons-mdi/floor-plan";
import backburgerIcon from "@iconify/icons-mdi/backburger";
import downloadIcon from "@iconify/icons-mdi/download";
import paletteIcon from "@iconify/icons-mdi/palette";
import paletteOutlineIcon from "@iconify/icons-mdi/palette-outline";
import { useTranslation } from "react-i18next";
import { IFloorPayload } from "../../../Domain/Types/FloorPlan/FloorPayload.type";
import { HistoryStateActionType } from "../../../Hooks/useHistoryState/useHistoryState";
import { useExportFloorPlanToExcel } from "../../../Hooks/useExportFloorPlanToExcel";
import { RemoteCompany } from "../../../../../hooks/Remote/Companies/useRemoteFetchCompanies";
import { CompanyUnits } from "../../../../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { Colleague } from "../../../../../features/Connections/types/Colleague.type";
import { LoadingButtonStyledText } from "../../../../Common/ButtonStyledText.partial";
import { ZoneColorMode } from "../../Views/CreateFloorPlanView/CreateFloorPlanView";

interface Props {
  floorPlanName: string;
  floorPlan: IFloorPayload;
  qrCodeOpen: boolean;
  setQrCodeOpen: (o: boolean) => void;
  showId: boolean;
  setShowId: (s: boolean) => void;
  onClickPublish: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onClickAbort: () => void;
  isEditable: boolean;
  enablePublish: boolean;
  isFloorPlanForService: boolean;
  remoteUnits: {
    remoteCompanies: RemoteCompany[] | undefined;
    remoteCompanyUnits: CompanyUnits[] | undefined;
    remoteUsers: Colleague[] | undefined;
  };
  zoneColorMode: ZoneColorMode;
  setZoneColorMode: (mode: ZoneColorMode) => void;
  dispatchFloorPlan: (action: {
    type: HistoryStateActionType.UNDO | HistoryStateActionType.REDO;
  }) => void;
  printFloorPlan: () => void;
  changeFloorFrame: () => void;
  wipeSelection: () => void;
}

export function TopToolbar({
  floorPlanName,
  onClickPublish,
  canUndo,
  canRedo,
  floorPlan,
  qrCodeOpen,
  setQrCodeOpen,
  showId,
  setShowId,
  onClickAbort,
  printFloorPlan,
  changeFloorFrame,
  isEditable,
  enablePublish,
  isFloorPlanForService,
  remoteUnits,
  dispatchFloorPlan,
  wipeSelection,
  zoneColorMode,
  setZoneColorMode
}: PropsWithChildren<Props>) {
  const theme = useTheme();
  const { t } = useTranslation();

  const iconWidth = 26;
  const iconHeight = 26;
  const iconColor = theme.palette.mode === "light" ? "black" : "white";
  const disabledUndoRedoColor = "grey";

  const { exportToExcel } = useExportFloorPlanToExcel(floorPlan, floorPlanName, remoteUnits);

  const checkEnablePublish = (() => {
    if (isFloorPlanForService) return true;
    return !isEditable || enablePublish;
  })();

  const publishToolTip = (() => {
    if (!isEditable) return t("PublishActiveCannotBeUpdated");
    if (isFloorPlanForService) return t("PublishServiceFloorPlan");

    if (enablePublish) return t("PublishInventoriesRequirement");

    return t("Finish Editing");
  })();

  return (
    <Box
      data-testid="plan-designer-top-toolbar-box"
      m={1}
      zIndex={10}
      height={50}
      sx={theme => ({
        border: "1px solid #00000088",
        backgroundColor: "#000000",
        borderRadius: 2,
        opacity: 0.95,
        flex: 1,
        ...theme.applyStyles("light", { backgroundColor: "#ffffff" })
      })}
      display={"flex"}
      alignItems={"center"}
    >
      <Tooltip title={<Typography>{t("Go back")}</Typography>} aria-label={"Go back"}>
        <IconButton data-testid="tool-btn-goBack" onClick={onClickAbort} size="large">
          <Icon icon={backburgerIcon} width={iconWidth} height={iconHeight} />
        </IconButton>
      </Tooltip>

      <Typography>{floorPlanName}</Typography>

      <Box flex={1} />

      {isEditable && (
        <>
          {/* undo the changes */}
          <Tooltip title={<Typography>{t("Undo")}</Typography>} aria-label={"Undo"}>
            <span>
              <IconButton
                data-testid="tool-btn-undo"
                onClick={() => {
                  wipeSelection();
                  dispatchFloorPlan({ type: HistoryStateActionType.UNDO });
                }}
                disabled={!canUndo}
                size="large"
              >
                <Icon
                  icon={undoIcon}
                  color={canUndo ? iconColor : disabledUndoRedoColor}
                  width={iconWidth}
                  height={iconHeight}
                />
              </IconButton>
            </span>
          </Tooltip>

          {/* redo the changes */}
          <Tooltip title={<Typography>{t("Redo")}</Typography>} aria-label={"Redo"}>
            <span>
              <IconButton
                data-testid="tool-btn-redo"
                onClick={() => {
                  wipeSelection();
                  dispatchFloorPlan({ type: HistoryStateActionType.REDO });
                }}
                disabled={!canRedo}
                size="large"
              >
                <Icon
                  icon={redoIcon}
                  color={canRedo ? iconColor : disabledUndoRedoColor}
                  width={iconWidth}
                  height={iconHeight}
                />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}

      <Divider color={"#aaa"} orientation={"vertical"} flexItem sx={{ opacity: "0.6" }} />

      {/* show id toggle */}
      <Tooltip
        title={<Typography>{showId ? t("_toShowingId") : t("_toHideShowingId")}</Typography>}
        aria-label={showId ? t("_toShowingId") : t("_toHideShowingId")}
      >
        <IconButton data-testid="tool-btn-showId" onClick={() => setShowId(!showId)} size="large">
          <Icon icon={identifierIcon} color={iconColor} width={iconWidth} height={iconHeight} />
        </IconButton>
      </Tooltip>

      {/* show id toggle */}
      <Tooltip
        title={
          <Typography>
            {zoneColorMode === "NORMAL"
              ? t("_switchZoneColorNormal")
              : t("_switchZoneColorDepartment")}
          </Typography>
        }
        aria-label={
          zoneColorMode === "NORMAL" ? t("_switchZoneColorNormal") : t("_switchZoneColorDepartment")
        }
      >
        <IconButton
          data-testid="tool-btn-zoneColorSwitch"
          onClick={() => {
            if (zoneColorMode === "NORMAL") return setZoneColorMode("BYDEPARTMENT");
            /** later, if (zoneColorMode === "BYDEPARTMENT") return setZoneColorMode("INDIVIDUAL"); **/
            return setZoneColorMode("NORMAL");
          }}
          size="large"
        >
          <Icon
            icon={zoneColorMode === "NORMAL" ? paletteIcon : paletteOutlineIcon}
            color={iconColor}
            width={iconWidth}
            height={iconHeight}
          />
        </IconButton>
      </Tooltip>

      {/* print qr codes */}
      <Tooltip
        title={<Typography>{t("Print QR codes for Workplaces")}</Typography>}
        aria-label={"Print QR codes for Workplaces"}
      >
        <IconButton
          data-testid="tool-btn-printQr"
          onClick={() => setQrCodeOpen(!qrCodeOpen)}
          size="large"
        >
          <Icon icon={qrcodeIcon} color={iconColor} width={iconWidth} height={iconHeight} />
        </IconButton>
      </Tooltip>

      {/* export as excel */}
      <Tooltip title={<Typography>{t("Export as XLSX")}</Typography>} aria-label={"Export as XLSX"}>
        <span>
          <IconButton data-testid="tool-btn-excel" size="large" onClick={exportToExcel}>
            <Icon icon={fileExcel} color={iconColor} width={iconWidth} height={iconHeight} />
          </IconButton>
        </span>
      </Tooltip>

      {/* change background picture of floor frame */}
      {isEditable && !isFloorPlanForService && (
        <Tooltip
          title={<Typography>{t("Change frame of plan_toolbar")}</Typography>}
          aria-label={"Change frame of plan_toolbar"}
        >
          <span>
            <IconButton
              data-testid="tool-btn-change-floorFrame"
              onClick={changeFloorFrame}
              size="large"
            >
              <Icon icon={floorPlanIcon} color={iconColor} width={iconWidth} height={iconHeight} />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* print floor plan as pdf */}
      <Tooltip
        title={<Typography>{t("Print Floor Plan")}</Typography>}
        aria-label={"Print Floor Plan"}
      >
        <IconButton data-testid="tool-btn-printFloor" onClick={printFloorPlan} size="large">
          <Icon icon={downloadIcon} color={iconColor} width={iconWidth} height={iconHeight} />
        </IconButton>
      </Tooltip>

      {/* button to change background image for the floor. it relates to viewport size */}
      {/* <Tooltip title={<Typography>{t("Modify Floor Frame")}</Typography>} aria-label={"Modify Floor Frame"}>
        <IconButton disabled>
          <Icon icon={wallpaperIcon} color={iconColor} width={iconWidth} height={iconHeight} />
        </IconButton>
      </Tooltip> */}

      <Divider color={"#aaa"} orientation={"vertical"} flexItem sx={{ opacity: "0.6" }} />

      {/* publish the floor plan */}
      <Tooltip title={<Typography>{publishToolTip}</Typography>} aria-label={publishToolTip}>
        <span>
          <LoadingButtonStyledText
            loading={false}
            theme={theme}
            data-testid="tool-btn-publish"
            onClick={onClickPublish}
            disabled={checkEnablePublish}
            sx={{ py: 0, px: 1, height: 48 }}
          >
            <Typography fontSize={12}>{t("Finish Editing")}</Typography>
          </LoadingButtonStyledText>
        </span>
      </Tooltip>
    </Box>
  );
}
